import { randomBytes } from "node:crypto";
import { mkdir, open, rename, statfs, unlink } from "node:fs/promises";
import { dirname } from "node:path";

// T5.8 — Linux filesystem magic numbers (from `<linux/magic.h>`).
// Used by `detectNetworkFsName` to identify the parent directory's
// filesystem type from a `statfs()` return value. The four entries
// below cover the network/FUSE cases where `rename()` is best-effort
// rather than strictly atomic; everything else is treated as local.
const NETWORK_FS_MAGIC: ReadonlyMap<number, string> = new Map([
  [0x6969, "nfs"],
  [0x517b, "smb"],
  [0xff534d42, "cifs"],
  [0x65735546, "fuse"],
]);

/**
 * T5.8 — Map a `statfs().type` magic number to a network-FS label, or
 * `null` for local filesystems. Pure function — exported via the
 * `__TESTING__` seam so unit tests can drive the parse logic without
 * needing a network mount.
 *
 * @internal
 */
function detectNetworkFsName(typeMagic: number): string | null {
  return NETWORK_FS_MAGIC.get(typeMagic) ?? null;
}

const warnedNfsDirs = new Set<string>();

/**
 * T5.8 — Best-effort one-shot stderr warning when `dirPath` lives on a
 * network/FUSE filesystem. Silent no-op on local filesystems, on
 * statfs failure (Windows / Node < 18.15 / EACCES), or after the
 * first warning per (dir + label) pair. Mirrors the `sqlite-wal.ts`
 * warn-once-per-label pattern (D63).
 *
 * @internal
 */
async function warnOnNetworkFsOnce(dirPath: string, label: string): Promise<void> {
  const key = `${dirPath}\0${label}`;
  if (warnedNfsDirs.has(key)) return;
  warnedNfsDirs.add(key);
  try {
    const info = await statfs(dirPath);
    const fsName = detectNetworkFsName(info.type);
    if (fsName === null) return;
    process.stderr.write(
      `[theokit-sdk] ${label}: detected network fs (${fsName}) at ${dirPath} — ` +
        "rename() atomicity guarantees may be weaker than expected.\n",
    );
  } catch {
    // statfs unavailable (Windows / Node < 18.15) or unreadable —
    // silent fallback. The warning is purely informational.
  }
}

/**
 * T5.8 — Test seam exposing the pure detection function so unit tests
 * can assert magic-number coverage without spinning up a network FS.
 * NOT included in the public barrel.
 *
 * @internal
 */
export function __TESTING__detectNetworkFsName(typeMagic: number): string | null {
  return detectNetworkFsName(typeMagic);
}

/**
 * T5.8 — Test seam: clear the per-directory warn-once registry between
 * tests so warning-emission tests stay deterministic.
 *
 * @internal
 */
export function __TESTING__resetNfsWarnings(): void {
  warnedNfsDirs.clear();
}

/**
 * Atomic file replacement: write content to a per-call unique tmp path,
 * fsync, then rename over the target. Crash mid-write leaves either the old
 * file intact or the new file complete — never a half-written file.
 *
 * The tmp suffix is `<pid>.<rand>.tmp` so parallel processes (and concurrent
 * burst writes within one process) never collide on the same tmp path — a
 * race that would manifest as `ENOENT` on `rename` after the rival process
 * already moved its tmp into place.
 *
 * Mirrors OpenClaw's `replaceFileAtomic` from
 * `referencia/openclaw/packages/memory-host-sdk/src/host/fs-utils.ts` with
 * the multi-writer robustness fix.
 *
 * @internal
 */
export async function replaceFileAtomic(filePath: string, content: string): Promise<void> {
  // T5.8 — warn once per parent directory if it lives on a network /
  // FUSE filesystem where `rename()` atomicity is best-effort. The
  // write proceeds unchanged; the warning is purely informational so
  // operators can spot the case in stderr / log aggregators.
  await warnOnNetworkFsOnce(dirname(filePath), "atomic-write");
  // T5.7 — crypto-random tmp suffix (CSPRNG, 64 bits of entropy)
  // replaces the predictable `Math.random().toString(36)` source. An
  // attacker observing the process can no longer predict the next
  // tmp path and pre-stage a hostile file to be renamed into place.
  const suffix = randomBytes(8).toString("hex");
  const tmp = `${filePath}.${process.pid}.${suffix}.tmp`;
  // T5.7 — mode 0o600 on the tmp file (owner read+write only). The
  // tmp file holds the FULL in-flight content (credential snapshots,
  // OAuth tokens) before the rename. World-readable default would
  // expose secrets during the ms-window between open and rename
  // (TOCTOU). On modern Linux the post-rename target inherits the
  // tmp's permission bits, so the final file is also 0o600.
  const handle = await open(tmp, "w", 0o600);
  try {
    await handle.writeFile(content, "utf8");
    await handle.sync();
  } finally {
    await handle.close();
  }
  try {
    await rename(tmp, filePath);
  } catch (cause) {
    // Cleanup tmp on rename failure so we don't leak stale .tmp files.
    await unlink(tmp).catch(() => undefined);
    throw cause;
  }
}

/**
 * Options for `atomicWriteJson`.
 *
 * @internal
 */
export interface AtomicWriteJsonOptions {
  /** Indent passed to `JSON.stringify`. Default: 2. */
  indent?: number;
  /** Whether to append a trailing newline (POSIX convention). Default: true. */
  trailingNewline?: boolean;
}

/**
 * Typed JSON atomic write helper.
 *
 * Serializes `data` to JSON, then delegates to `replaceFileAtomic`. The
 * parent directory is auto-created (recursive `mkdir`) to make this helper
 * safe for callers who haven't ensured the directory exists (EC-4 in the
 * persistence-state-hardening plan).
 *
 * Throws `TypeError` on circular refs or `undefined` data (propagates from
 * `JSON.stringify`).
 *
 * @internal
 */
export async function atomicWriteJson<T>(
  filePath: string,
  data: T,
  options?: AtomicWriteJsonOptions,
): Promise<void> {
  const indent = options?.indent ?? 2;
  const trailingNewline = options?.trailingNewline ?? true;
  const json = JSON.stringify(data, null, indent);
  if (json === undefined) {
    throw new TypeError("atomicWriteJson: cannot serialize undefined");
  }
  const content = trailingNewline ? `${json}\n` : json;
  await mkdir(dirname(filePath), { recursive: true });
  await replaceFileAtomic(filePath, content);
}

/**
 * Atomic text write. Same crash-safety guarantees as `replaceFileAtomic` +
 * auto-mkdir of the parent directory. Used by `theokit-migrate-config`
 * (T4.1, EC-2 MUST FIX) so a crash mid-migration leaves previous MD files
 * intact rather than corrupting them.
 *
 * @internal
 */
export async function atomicWriteText(filePath: string, content: string): Promise<void> {
  await mkdir(dirname(filePath), { recursive: true });
  await replaceFileAtomic(filePath, content);
}
